export const initialPlayerValues = () => ({
  firstName: '', lastName: '', email: '', phone: '', birthDate: '', gender: '', dominantHand: '', tshirtSize: '',
  country: '', location: null, favoriteSport: '', singles: '', doubles: '', isOrganizer: false,
  organizerClientType: '', organizerFirstName: '', organizerLastName: '', organizerEmail: '', organizerPhone: '',
  organizerCompany: '', organizerRegistrationNumber: '', organizerCountry: '', organizerLocation: null,
  organizerClubs: [], organizerOSAccess: '',
})
const clean = value => value.trim().normalize('NFC')
// The shared city picker adds Google county data, which the API accepts only on the player's own location.
const withoutGooglePlace = location => location && Object.fromEntries(['name', 'city', 'country', 'coords'].map(key => [key, location[key]]))
export const buildPlayerCreationPayload = values => ({
  firstName: clean(values.firstName), lastName: clean(values.lastName), email: clean(values.email).toLowerCase(),
  phone: values.phone, birthDate: values.birthDate, gender: values.gender,
  dominantHand: values.dominantHand || null, tshirtSize: values.tshirtSize || null,
  ...(values.isOrganizer ? { organizerInfo: {
    clientType: values.organizerClientType, firstName: clean(values.organizerFirstName), lastName: clean(values.organizerLastName),
    email: clean(values.organizerEmail).toLowerCase(), phone: values.organizerPhone, location: withoutGooglePlace(values.organizerLocation),
    assignedClubs: values.organizerClubs.map(club => club.value), osAccess: values.organizerOSAccess === 'true',
    ...(values.organizerClientType === 'company' ? { companyName: clean(values.organizerCompany),
      uniqueRegistrationNumber: clean(values.organizerRegistrationNumber) } : {}),
  } } : {}),
  favoriteSport: values.favoriteSport, gameLevel: { singles: values.singles ? Number(values.singles) : null,
    doubles: values.doubles ? Number(values.doubles) : null }, location: values.location, isOrganizer: values.isOrganizer,
})
export const playerLevelOptions = (settings, sport) => (settings?.levels || [])
  .filter(level => !level.sportType || level.sportType === sport)
  .map(level => Number(level.key)).filter(level => Number.isInteger(level) && level >= 4 && level <= 9)

export const validatePlayerCreation = (values, settings, today = new Date().toISOString().slice(0, 10)) => {
  const errors = {}
  const name = (field, label) => {
    if (!/^[\p{L}\p{M}][\p{L}\p{M} '\u2019-]*$/u.test(values[field].trim()) || values[field].trim().length > 100) {
      errors[field] = `Enter ${label.toLowerCase()} using letters, spaces, apostrophes or hyphens.`
    }
  }
  const email = field => {
    if (values[field].trim().length > 254 || !/^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/.test(values[field].trim())) errors[field] = 'Enter a valid email address.'
  }
  const location = (countryField, locationField) => {
    if (!settings?.eligibleCountries?.some(country => country.name === values[countryField])) errors[countryField] = 'Select an available country.'
    const selected = values[locationField]
    if (!selected?.city || selected.country !== values[countryField] || !Number.isFinite(selected.coords?.lat)
      || !Number.isFinite(selected.coords?.lng)) errors[locationField] = 'Select a city in the chosen country.'
  }
  name('firstName', 'First name'); name('lastName', 'Last name'); email('email')
  const date = values.birthDate
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(date))
    || new Date(date).toISOString().slice(0, 10) !== date || date > today) errors.birthDate = 'Enter a valid birth date that is not in the future.'
  if (!['male', 'female'].includes(values.gender)) errors.gender = 'Select a gender.'
  if (!settings?.activeSports?.includes(values.favoriteSport)) errors.favoriteSport = 'Select an available sport.'
  for (const game of ['singles', 'doubles']) {
    if (values[game] !== '' && !playerLevelOptions(settings, values.favoriteSport).includes(Number(values[game]))) errors[game] = 'Select an available level or leave it unknown.'
  }
  location('country', 'location')
  if (values.isOrganizer) {
    if (!['physicalPerson', 'company'].includes(values.organizerClientType)) errors.organizerClientType = 'Choose Individual or Company.'
    name('organizerFirstName', 'First name'); name('organizerLastName', 'Last name'); email('organizerEmail')
    location('organizerCountry', 'organizerLocation')
    if (!['true', 'false'].includes(values.organizerOSAccess)) errors.organizerOSAccess = 'Choose whether to grant SportyaOS access.'
    if (values.organizerClientType === 'company') {
      if (!values.organizerCompany.trim()) errors.organizerCompany = 'Enter the company name.'
      if (!values.organizerRegistrationNumber.trim()) errors.organizerRegistrationNumber = 'Enter the registration number.'
    }
  }
  return errors
}
